import { useEffect, useMemo, useState } from 'react'
import { useStytchMember } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import { useUpdateDocument } from '@/service-library/hooks/documents'
import { useBulkSplitCreateDocuments } from '@/service-library/hooks/split-document'
import queryKeys from '@/service-library/query-keys'
import { Document } from '@/types/documents'
import { showErrorSnackbar, showInfoSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useSelectedWorkflowContext } from '../workflows/SelectedWorkflowProvider'

type SplitDocumentViewProps = {
  document: Document
  documentQueryKey?: QueryKey
}

function getPagesFromSplitPoints(
  splitPoints: number[],
  totalPoints: number,
  deletedPages: number[],
) {
  const newDocumentsPages = []
  let startPage = 1
  splitPoints.forEach((point) => {
    if (point >= totalPoints) return

    const pages = Array.from(
      { length: point - startPage + 1 },
      (_, index) => startPage + index,
    )

    const filteredPages = pages.filter((page) => !deletedPages.includes(page))
    if (filteredPages.length) newDocumentsPages.push(filteredPages)
    startPage = point + 1
  })

  if (startPage <= totalPoints) {
    const lastDocumentPages = Array.from(
      { length: totalPoints - startPage + 1 },
      (_, index) => startPage + index,
    )

    const filteredPages = lastDocumentPages.filter(
      (page) => !deletedPages.includes(page),
    )
    if (filteredPages.length) newDocumentsPages.push(filteredPages)
  }

  return newDocumentsPages
}

function splitPointsToPageGroups(
  splitPoints: number[],
  totalPages: number,
): number[][] {
  return getPagesFromSplitPoints(splitPoints, totalPages, [])
}

export default function useSplitDocuments({
  document,
  documentQueryKey,
}: SplitDocumentViewProps) {
  const { member } = useStytchMember()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const [pagesPerSplit, setPagesPerSplit] = useState<number>(1)
  const [pagesToDelete, setPagesToDelete] = useState<number[]>([])

  const pages = document.document_pages

  const { updateDocument } = useUpdateDocument({
    detailQueryKey:
      documentQueryKey || queryKeys.documents.detail({ id: document.id }),
  })

  // Derive split points from document.suggested_splits
  const suggestedSplits = document.suggested_splits

  const splitPoints = useMemo(() => {
    if (!suggestedSplits?.splits?.length) return []
    return suggestedSplits.splits
      .slice(0, -1)
      .map((group) => Math.max(...group))
      .sort((a, b) => a - b)
  }, [suggestedSplits])

  const setSplitPoints = (newSplitPoints: number[]) => {
    const newSplits = splitPointsToPageGroups(newSplitPoints, pages.length)
    updateDocument({
      ...document,
      suggested_splits:
        newSplits.length > 0
          ? { suggested_by_ai: false, splits: newSplits }
          : null,
    })
  }

  const { bulkSplitCreateDocumentsAsync, isPending } =
    useBulkSplitCreateDocuments({
      onError: () => {
        showErrorSnackbar(
          'Some documents may have failed to create. Please try again later.',
          { autoHideDuration: 3000 },
        )
      },
    })

  const createDocuments = () => {
    showInfoSnackbar('Splitting Document')

    const newDocumentsPages = getPagesFromSplitPoints(
      splitPoints,
      pages.length,
      pagesToDelete,
    )

    const newDocuments = newDocumentsPages.map((pages) => ({
      id: generateUuid(),
      pages,
      workflow_id: selectedWorkflow?.id,
    }))

    return bulkSplitCreateDocumentsAsync({
      document_id: document.id,
      documents: newDocuments,
      submitter: member?.name,
      submitter_email: member?.email_address,
    })
  }

  useEffect(() => {
    setPagesToDelete([])
  }, [document.id])

  return {
    isPending,
    pagesPerSplit,
    pagesToDelete,
    splitPoints,
    createDocuments,
    setPagesPerSplit,
    setPagesToDelete,
    setSplitPoints,
  }
}
